import React, { useEffect, useState, useCallback } from "react";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Briefcase, Users, BookOpen, Layers3, BarChart3, Plus, Search, Loader2 } from "lucide-react";
import EngagementCreateModal from "../components/EngagementCreateModal";
import EngagementDrawer from "../components/EngagementDrawer";
import ProgrammesTab from "../components/ProgrammesTab";
import OutcomesTab from "../components/OutcomesTab";
import FacultyTab from "../components/FacultyTab";
import CurriculumTab from "../components/CurriculumTab";
import { toast } from "sonner";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STATUS_COLORS = {
  STARTING: "bg-sky-100 text-sky-800 border-sky-300",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
  WINDING_DOWN: "bg-amber-100 text-amber-800 border-amber-300",
  COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ON_HOLD: "bg-slate-100 text-slate-700 border-slate-300",
};

function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP", maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency || ""} ${v}`;
  }
}

function ActiveTab({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [myWeek, setMyWeek] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (myWeek) params.set("my_week", "true");
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/engagements?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error(`Failed (${r.status})`);
      setItems(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, myWeek]);

  useEffect(() => { load(); }, [load]);

  // Open drawer from ?focus=eng_id (e.g., redirected from closed-won handshake)
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus) {
      setDrawerId(focus);
      const sp = new URLSearchParams(searchParams);
      sp.delete("focus");
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = items.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (e.name || "").toLowerCase().includes(q) ||
      (e.client_name || "").toLowerCase().includes(q) ||
      (e.lead_owner_email || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="engagements-active-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <Input
            placeholder="Search by name, client or owner…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-8"
            data-testid="engagements-search-input"
          />
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="engagements-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New engagement
        </Button>
        <Button
          variant={myWeek ? "default" : "outline"}
          onClick={() => setMyWeek(!myWeek)}
          data-testid="engagements-myweek-toggle"
        >
          {myWeek ? "My week ✓" : "My week"}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading engagements…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="engagements-empty">
              <Briefcase className="h-8 w-8 mx-auto text-slate-300 mb-2" />
              {items.length === 0
                ? "No engagements yet. Create one or move a deal to Closed-Won."
                : "No matches."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <th className="px-3 py-2">Engagement</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Value</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Dates</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.engagement_id}
                    className="border-t border-slate-100 cursor-pointer hover:bg-blue-50/40"
                    onClick={() => setDrawerId(e.engagement_id)}
                    data-testid={`engagement-row-${e.engagement_id}`}
                  >
                    <td className="px-3 py-2 font-medium text-slate-800">{e.name}</td>
                    <td className="px-3 py-2 text-slate-600">{e.client_name || "—"}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-xs ${STATUS_COLORS[e.status] || ""}`}>
                        {e.status?.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(e.contracted_value, e.currency)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{e.lead_owner_email || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {e.start_date || "—"} → {e.end_date || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <EngagementCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        workspaceId={workspaceId}
        onCreated={(created) => {
          load();
          if (created?.engagement_id) setDrawerId(created.engagement_id);
        }}
      />

      <EngagementDrawer
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        workspaceId={workspaceId}
        engagementId={drawerId}
        onChanged={load}
      />
    </div>
  );
}

function PlaceholderPane({ title, blurb }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-6 text-sm">
        <div className="font-semibold text-slate-700 capitalize mb-1">{title}</div>
        <p className="text-slate-500">{blurb}</p>
        <p className="text-xs text-slate-400 mt-3">Lands in a later phase of the REX v3 build.</p>
      </CardContent>
    </Card>
  );
}

export default function EngagementsPage() {
  const [workspaceId, setWorkspaceId] = useState(null);
  const [tab, setTab] = useState("active");
  const [drawerId, setDrawerId] = useState(null);

  useEffect(() => {
    fetchMyWorkspaces()
      .then((rows) => rows?.[0] && setWorkspaceId(rows[0].workspace_id))
      .catch(() => {});
  }, []);

  const tabs = [
    { key: "active", label: "Active", icon: Briefcase },
    { key: "programmes", label: "Programmes", icon: Layers3 },
    { key: "faculty", label: "Faculty", icon: Users },
    { key: "curriculum", label: "Curriculum", icon: BookOpen },
    { key: "outcomes", label: "Outcomes", icon: BarChart3 },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="engagements-page">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Engagements</h1>
        <span className="text-xs text-slate-500">Delivery orchestration</span>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`engagements-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors ${
                active
                  ? "border-blue-600 text-blue-700 font-semibold"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "active" && <ActiveTab workspaceId={workspaceId} />}
      {tab === "programmes" && (
        <ProgrammesTab
          workspaceId={workspaceId}
          onEngagementCreated={(d) => {
            setTab("active");
            if (d?.engagement_id) setDrawerId(d.engagement_id);
          }}
        />
      )}
      {tab === "faculty" && <FacultyTab workspaceId={workspaceId} />}
      {tab === "curriculum" && <CurriculumTab workspaceId={workspaceId} />}
      {tab === "outcomes" && (
        <OutcomesTab
          workspaceId={workspaceId}
          onOpenEngagement={(id) => { setTab("active"); setDrawerId(id); }}
        />
      )}

      {/* Cross-tab drawer trigger (used by Programmes 'Use' + Outcomes click-through) */}
      <EngagementDrawer
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        workspaceId={workspaceId}
        engagementId={drawerId}
      />
    </div>
  );
}
